/**************************************************************************************************************************
 Copyright (c) 2024 WorkEarly Results LLP

     Name: about-us.section.tsx
   Author: Harikrishnan Gangadharan
 Comments: 

/**************************************************************************************************************************
 IMPORTS
***************************************************************************************************************************/
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import Statics from '@/constants/statics';
import BasicComponents from '@/shared/basic.components';
import Section from '@/shared/section';
import Assets from '@/constants/assets';
/**************************************************************************************************************************
 IMPLEMENTATIONS
***************************************************************************************************************************/
const WhoAndWhy: FunctionComponent = () => {
  const { t } = useTranslation('home-page');

  return (
    <Section id="why-us" className="bg-black/80">
      <div className="w-full grid grid-cols-1 gap-10">
        <div className="col-span-1 flex flex-col items-start justify-start gap-5">
          <BasicComponents.SectionHeader
            title={t('about-us.title')}
            subtitle={t('about-us.subtitle')}
          />
          <div className="w-full flex flex-col items-center justify-start gap-3">
            <motion.p
              className="w-full text-md lg:text-lg text-justify text-white font-roboto font-light"
              initial="initial"
              whileInView="entry"
              viewport={{ once: true }}
              variants={{
                initial: {
                  x: -200,
                  opacity: 0
                },
                entry: {
                  x: 0,
                  opacity: 1
                }
              }}
              transition={{
                duration: 0.5
              }}
            >
              {t('about-us.desc')}
            </motion.p>
          </div>
        </div>
        <div className="col-span-1 flex flex-col items-start justify-start gap-5">
          <BasicComponents.SectionHeader
            title={t('why-us.title')}
            subtitle={t('why-us.subtitle')}
          />
          <div className="w-full flex flex-col items-center justify-start gap-3">
            {Statics.UniquePoints.map(({ id, point }, idx: number) => (
              <motion.div
                key={id}
                className="w-full flex items-center justify-start gap-[20px]"
                initial="initial"
                whileInView="entry"
                viewport={{ once: true }}
                variants={{
                  initial: {
                    x: 50 + idx * 25,
                    opacity: 0
                  },
                  entry: {
                    x: 0,
                    opacity: 1
                  }
                }}
                transition={{
                  duration: 0.5
                }}
              >
                <div className="w-[15px] h-[15px] bg-theme-900 rounded-full flex flex-col items-center justify-center">
                  <div className="w-[8px] h-[8px] bg-black/80 rounded-full flex flex-col items-center justify-center"></div>
                </div>
                <p className="w-[calc(100%-35px)] text-md lg:text-lg text-white font-roboto font-light">
                  {t(point)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
};

const OurMission: FunctionComponent = () => {
  const { t } = useTranslation('about-us');

  return (
    <Section id="our-mission" className="bg-black/80">
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="col-span-1 flex flex-col items-start justify-start gap-5">
          <BasicComponents.SectionHeader
            title={t('mission.title')}
            subtitle={t('mission.subtitle')}
          />
          <motion.p
            className="w-[calc(100%-35px)] text-md lg:text-lg text-justify text-white font-roboto font-light"
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                x: -50,
                opacity: 0
              },
              entry: {
                x: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5
            }}
          >
            {t('mission.desc')}
          </motion.p>
        </div>
        <div className="col-span-1 flex flex-col items-start justify-start gap-5">
          <motion.img
            src={Assets.Images.MISSION.src}
            alt={Assets.Images.MISSION.alt}
            initial="initial"
            whileInView="entry"
            viewport={{ once: true }}
            variants={{
              initial: {
                x: 100,
                opacity: 0
              },
              entry: {
                x: 0,
                opacity: 1
              }
            }}
            transition={{
              duration: 0.5
            }}
          />
        </div>
      </div>
    </Section>
  );
};

const OurVision: FunctionComponent = () => {
  const { t } = useTranslation('about-us');

  return (
    <Section id="our-vision" className="bg-black/80">
      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-5">
        <motion.div
          className="col-span-1 order-last lg:order-first flex flex-col items-start justify-start gap-10"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: -100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          <img src={Assets.Images.VISION.src} alt={Assets.Images.VISION.alt} />
        </motion.div>
        <motion.div
          className="col-span-1 flex flex-col items-start justify-start gap-5"
          initial="initial"
          whileInView="entry"
          viewport={{ once: true }}
          variants={{
            initial: {
              x: 100,
              opacity: 0
            },
            entry: {
              x: 0,
              opacity: 1
            }
          }}
          transition={{
            duration: 0.5
          }}
        >
          <BasicComponents.SectionHeader
            title={t('vision.title')}
            subtitle={t('vision.subtitle')}
          />
          <p className="w-[calc(100%-35px)] text-md lg:text-lg text-justify text-white font-roboto font-light">
            {t('vision.desc')}
          </p>
        </motion.div>
      </div>
    </Section>
  );
};

const AboutUs = {
  WhoAndWhy,
  OurMission,
  OurVision
};

export default AboutUs;
